import { useRef } from "react";
import { motion, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Check } from "lucide-react";
import { EASE, Eyebrow } from "./Reveal";
import { Wash, Sprig } from "./Watercolor";

const HERO_IMG =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_custom-umbrellas/artifacts/deda2c668928ca18_IMG_3004.jpeg";

const LINES = ["Rain, meet", "your match,", "made to bloom."];
const PILLS = ["100% waterproof canopy", "UV-50+ sun shield", "Ships in a gift box"];

export default function Hero({ onNavigate }) {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 14 });
  const rotX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 14 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMove}
      className="relative flex items-center min-h-screen pt-28 pb-16 sm:pt-32"
      data-testid="hero-section"
    >
      <Wash color="#FFD166" size={520} opacity={0.38} className="-top-16 -left-32" />
      <Wash color="#F472B6" size={420} opacity={0.26} className="-bottom-10 -right-24" />
      <Sprig color="#F472B6" className="hidden lg:block left-[45%] top-14 w-24 opacity-90" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1, ease: EASE }}>
            <Eyebrow data-testid="hero-eyebrow">Ella's Umbrellas — hand-painted in small batches</Eyebrow>
          </motion.div>

          <h1 className="mt-6 font-serif font-medium tracking-tight leading-[1.04] text-ink text-5xl sm:text-6xl lg:text-[4.6rem]">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1 -mb-1">
                <motion.span
                  className={`block ${i === 2 ? "italic text-terracotta" : ""}`}
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.2 + i * 0.14, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65, ease: EASE }}
            className="mt-6 max-w-md text-base sm:text-lg leading-relaxed text-ink/70"
            data-testid="hero-subline"
          >
            Custom-made umbrellas, hand-painted petal by petal in Ella's little workshop.
            Pick a print, add your monogram, and carry a garden through any weather.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.78, ease: EASE }}
            className="mt-6 flex flex-wrap gap-2.5"
          >
            {PILLS.map((pill, i) => (
              <span
                key={pill}
                data-testid={`hero-pill-${i + 1}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-ink/10 bg-white/80 px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-wider text-ink/70"
              >
                <Check size={12} className="text-terracotta" />
                {pill}
              </span>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <motion.button
              whileHover={{ scale: 1.04, y: -2 }}
              whileTap={{ scale: 0.96 }}
              data-testid="hero-cta-commission"
              onClick={() => onNavigate("#order")}
              className="rounded-full bg-terracotta text-white font-semibold px-7 py-3.5 shadow-xl shadow-terracotta/25 hover:bg-terracotta/90 transition-colors"
            >
              Commission yours
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.04, y: -2 }}
              whileTap={{ scale: 0.96 }}
              data-testid="hero-cta-collection"
              onClick={() => onNavigate("#collection")}
              className="inline-flex items-center gap-2 rounded-full border-2 border-ink/15 bg-white/70 px-6 py-3 font-semibold text-ink hover:border-terracotta hover:text-terracotta transition-colors"
            >
              See the collection
              <ArrowDown size={16} />
            </motion.button>
          </motion.div>
        </div>

        <motion.div className="lg:col-span-5 relative mt-4 lg:mt-0" style={{ perspective: 1000, y: imgY }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.35, ease: EASE }}
            style={{ rotateX: rotX, rotateY: rotY, transformStyle: "preserve-3d" }}
            className="relative"
            data-testid="hero-visual"
          >
            <div className="aspect-[4/5] overflow-hidden rounded-t-[999px] rounded-b-[2.5rem] border-4 border-white shadow-2xl shadow-terracotta/20">
              <img
                src={HERO_IMG}
                alt="Hand-painted floral umbrella in the sun"
                className="h-full w-full object-cover"
                data-testid="hero-image"
              />
            </div>

            <div className="absolute -top-5 -right-3 sm:-right-5 h-24 w-24 sm:h-28 sm:w-28 rounded-full bg-terracotta shadow-xl shadow-terracotta/30">
              <svg viewBox="0 0 100 100" className="h-full w-full animate-spin-slow" aria-hidden="true">
                <defs>
                  <path id="circ" d="M50,50 m-36,0 a36,36 0 1,1 72,0 a36,36 0 1,1 -72,0" />
                </defs>
                <text fontSize="10" letterSpacing="2.6" fill="#FFFDF7" fontFamily="JetBrains Mono, monospace">
                  <textPath href="#circ">HAND-PAINTED ✺ ONE OF ONE ✺ </textPath>
                </text>
              </svg>
              <svg viewBox="0 0 24 24" className="absolute inset-0 m-auto h-8 w-8" aria-hidden="true">
                <path d="M12 2v20M2 12h20M4.9 4.9l14.2 14.2M19.1 4.9L4.9 19.1" stroke="#FFFDF7" strokeWidth="2.2" strokeLinecap="round" />
              </svg>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.05, ease: EASE }}
              className="absolute bottom-6 -left-3 sm:-left-6 rounded-full bg-white border border-ink/10 px-4 py-2 shadow-lg"
              data-testid="hero-batch-tag"
            >
              <span className="font-mono text-[11px] uppercase tracking-wider text-ink/70">
                Batch Nº 07 — 48 umbrellas
              </span>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
